import { ChangeEvent } from "react";
import { IoIosArrowUp } from "react-icons/io";
import { styled } from "styled-components";
import { designTokens, getTypography } from "styles/designTokens";
import { IconWrapper } from "../common/IconWrapper";
import InputField from "../InputField";

export const UnifiedDropdownContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// 스타일 컴포넌트들
export const DropdownContainer = styled.div`
  width: 100%;
  position: relative;
`;

interface DropdownHeaderProps {
  $isOpen: boolean;
  $selectedOption: string;
}

export const DropdownHeader = styled.div<DropdownHeaderProps>`
  width: 100%;
  height: 41px;
  border-radius: 4px;
  border: 1px solid rgba(222, 226, 230, 0.83);
  padding: 7px 13px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: ${({ $selectedOption }) =>
    $selectedOption === "" ? designTokens.colors.font.tertiary : "#5a5a5a"};
  ${getTypography("korean", "body3")}
`;

export const DropdownList = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 240px;
  margin-top: 4px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  padding: 8px 0;
  list-style: none;
  z-index: 10;
`;

interface DropdownItemProps {
  selected: boolean;
}

export const DropdownItem = styled.li<DropdownItemProps>`
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  color: ${(props) => (props.selected ? "#30B0C7" : "#8E8E93")};
  font-weight: ${(props) => (props.selected ? "700" : "400")};
  font-size: 16px;

  &:hover {
    background-color: #f1f1f1;
  }
`;

interface ArrowProps {
  $isOpen: boolean;
}

export const Arrow = styled.span<ArrowProps>`
  transition: transform 0.2s ease-in-out;
  transform: ${(props) => (props.$isOpen ? "rotate(0)" : "rotate(180deg)")};
`;

// UnifiedDropdown 컴포넌트
interface UnifiedDropdownProps<T extends string | null> {
  label: string;
  dropdownName: string;
  options: T[];
  selectedOption: T;
  setSelectedOption: (value: T) => void;
  customValue?: string;
  setCustomValue?: (value: string) => void;
  placeholder: string;
  error?: string | null;
  customError?: string | null;
  openDropdown: string | null;
  setOpenDropdown: (value: string | null) => void;
  showCustomInput?: boolean;
}

const UnifiedDropdown = <T extends string | null>({
  label,
  dropdownName,
  options,
  selectedOption,
  setSelectedOption,
  customValue = "",
  setCustomValue = () => {},
  placeholder,
  error,
  customError,
  openDropdown,
  setOpenDropdown,
  showCustomInput = true,
}: UnifiedDropdownProps<T>) => {
  const handleDropdownToggle = () => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const handleOptionClick = (option: T) => {
    setSelectedOption(option);
    setOpenDropdown(null);
    if (option !== "기타" && setCustomValue) {
      setCustomValue("");
    }
  };

  return (
    <UnifiedDropdownContainer>
      <DropdownContainer>
        <DropdownHeader
          $isOpen={openDropdown === dropdownName}
          $selectedOption={selectedOption || placeholder}
          onClick={handleDropdownToggle}
          style={{
            borderColor: error ? '#ff3b30' : undefined,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedOption || placeholder}
          <Arrow $isOpen={openDropdown === dropdownName}>
            <IconWrapper icon={IoIosArrowUp} />
          </Arrow>
        </DropdownHeader>
        
        {openDropdown === dropdownName && (
          <DropdownList>
            {options.map((option, index) => (
              <DropdownItem
                key={index}
                selected={selectedOption === option}
                onClick={() => handleOptionClick(option)}
              >
                {option}
              </DropdownItem>
            ))}
          </DropdownList>
        )}
      </DropdownContainer>
      
      {showCustomInput && selectedOption === "기타" && (
        <InputField
          label={label}
          type="text"
          value={customValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setCustomValue(e.target.value)
          }
          placeholder={`${label}을 직접 입력해주세요`}
          error={customError || undefined}
        />
      )}
      
      {error && (
        <div style={{ marginTop: "4px", color: "#ff3b30", fontSize: "14px" }}>
          {error}
        </div>
      )}
    </UnifiedDropdownContainer>
  );
};

export default UnifiedDropdown;
